<template>
  <div>
    <meal-rating-dialog
      v-model="dialog"
      :order="selectedOrder"
      :initial-rating="initialRating"
      @saved="onSaved"
    />

    <!-- Popup posle preuzimanja: brza ocena zvezdicama.
         Mobilni: panel odozdo; desktop: centriran dijalog -->
    <component
      :is="device.isMobile ? 'v-bottom-sheet' : 'v-dialog'"
      v-model="sheet"
      :max-width="device.isMobile ? undefined : 440"
      :content-class="device.isMobile ? '' : 'rating-popup-dialog'"
    >
      <v-sheet
        v-if="popupOrder"
        class="text-center"
        :class="device.isMobile ? 'rating-sheet--mobile px-4 pt-2 pb-6' : 'rating-sheet--desktop px-8 pt-4 pb-6'"
      >
        <div v-if="device.isMobile" class="sheet-handle mx-auto mb-4" />
        <div v-else class="d-flex justify-end mr-n4">
          <v-btn icon small @click="sheet = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <v-img
          v-if="popupImage"
          :src="popupImage"
          height="160"
          class="rounded-lg mb-3 grey lighten-3"
          cover
        />
        <div v-else class="sheet-icon mx-auto mb-3 d-flex align-center justify-center">
          <v-icon color="primary">mdi-silverware-fork-knife</v-icon>
        </div>
        <div class="body-2 success--text text--darken-1 mb-2">
          {{ pickedUpLabel(popupOrder) }}
        </div>
        <div class="text-h6 font-weight-bold black--text mb-2">
          {{ howWasLabel(popupOrder) }}
        </div>
        <div class="body-1 black--text">
          {{ translate(popupOrder.meals ? popupOrder.meals.name : "") }}
        </div>
        <div class="caption grey--text text--darken-1 mb-3">
          <span v-if="popupOrder.restaurant">{{ popupOrder.restaurant.name }}</span>
          <span v-if="popupOrder.restaurant && shiftNumber(popupOrder)"> · </span>
          <span v-if="shiftNumber(popupOrder)">
            {{ $t("meal_rating.shift", { n: shiftNumber(popupOrder) }) }}
          </span>
        </div>
        <v-rating
          v-model="sheetRating"
          color="amber"
          background-color="grey lighten-1"
          empty-icon="mdi-star-outline"
          size="44"
          hover
          class="mb-1"
        />
        <div class="body-2 font-weight-medium grey--text text--darken-2 mb-4">
          {{
            sheetRating
              ? $t(`meal_rating.labels.${sheetRating}`)
              : $t("meal_rating.tap_star")
          }}
        </div>
        <v-btn
          block
          x-large
          depressed
          color="primary"
          class="rounded-lg text-none font-weight-bold"
          :disabled="!sheetRating"
          @click="submitSheet"
        >
          {{ $t("meal_rating.submit") }}
        </v-btn>
        <v-btn text class="mt-2 text-none" @click="sheet = false">
          {{ $t("meal_rating.later") }}
        </v-btn>
      </v-sheet>
    </component>
  </div>
</template>

<script>
import userService from "@/services/user";
import moment from "moment";
import MealRatingDialog from "./MealRatingDialog.vue";
import { orderMealImageUrl } from "@/utils/mealImage";

// Obroci odloženi sa "Kasnije" — posle SNOOZE_HOURS popup ponovo iskače,
// sve dok obrok ne bude ocenjen ili ne istekne rok (backend ga tada ne vraća)
const SNOOZED_KEY = "mealRatingSnoozed";
const SNOOZE_HOURS = 3;

export default {
  name: "MealRatingPrompt",
  components: { MealRatingDialog },
  data: () => ({
    orders: [],
    // { [orderId]: timestamp do kog je obrok odložen }
    snoozed: {},
    sheet: false,
    popupOrder: null,
    sheetRating: 0,
    // Popup se zatvara jer prelazimo na detalje — tada ne odlaži obrok
    goingToDetails: false,
    dialog: false,
    selectedOrder: null,
    initialRating: 0,
    detailsSaved: false,
  }),
  computed: {
    popupImage() {
      return orderMealImageUrl(this.popupOrder);
    },
  },
  watch: {
    sheet(open) {
      if (open) return;
      if (this.goingToDetails) {
        this.goingToDetails = false;
        return;
      }
      // "Kasnije" ili klik van popupa
      if (this.popupOrder && this.isPending(this.popupOrder.id)) {
        this.snooze(this.popupOrder.id);
      }
    },
    dialog(open) {
      if (open) return;
      if (this.detailsSaved) {
        this.detailsSaved = false;
        this.showNextPopup();
      } else if (this.selectedOrder) {
        // Zatvoreni detalji bez slanja — ne nudi ponovo u ovoj sesiji
        this.snooze(this.selectedOrder.id);
      }
    },
  },
  created() {
    this.snoozed = this.readSnoozed();
    this.fetch();
  },
  methods: {
    fetch() {
      userService
        .getPendingMealRatings()
        .then((res) => {
          this.orders = Array.isArray(res.data) ? res.data : [];
          this.showNextPopup();
        })
        .catch(() => {
          // Popup je opcion — meni mora da radi i ako ovo pukne
          this.orders = [];
        });
    },
    isPending(id) {
      return this.orders.some((o) => o.id === id);
    },
    showNextPopup() {
      const now = Date.now();
      const order = this.orders.find((o) => !(this.snoozed[o.id] > now));
      if (!order) return;
      this.popupOrder = order;
      this.sheetRating = 0;
      // Mali delay da se meni (ili prethodni popup) prvo iscrta/zatvori
      setTimeout(() => {
        this.sheet = true;
      }, 600);
    },
    submitSheet() {
      // Uvek nastavi na ekran sa detaljima (šta nije bilo u redu, komentar)
      this.goingToDetails = true;
      this.sheet = false;
      this.selectedOrder = this.popupOrder;
      this.initialRating = this.sheetRating;
      this.dialog = true;
    },
    onSaved() {
      this.detailsSaved = true;
      this.removeOrder(this.selectedOrder.id);
    },
    removeOrder(id) {
      this.orders = this.orders.filter((o) => o.id !== id);
    },
    pickedUpLabel(order) {
      const day = moment(order.date).startOf("day");
      const today = moment().startOf("day");
      if (day.isSame(today)) return this.$t("meal_rating.picked_today");
      if (day.isSame(today.clone().subtract(1, "day"))) {
        return this.$t("meal_rating.picked_yesterday");
      }
      return this.$t("meal_rating.picked_on", { date: this.localeDate(order.date) });
    },
    howWasLabel(order) {
      const key = `meal_rating.how_was.${order.type}`;
      return this.$te(key) ? this.$t(key) : this.$t("meal_rating.how_was.default");
    },
    shiftNumber(order) {
      return parseInt(order.shift, 10) || null;
    },
    storageKey() {
      const userId = this.$store.state.user?.user?.id || "guest";
      return `${SNOOZED_KEY}:${userId}`;
    },
    snooze(id) {
      const now = Date.now();
      const snoozed = { [id]: now + SNOOZE_HOURS * 3600 * 1000 };
      // Zadrži samo odlaganja koja još važe
      Object.keys(this.snoozed).forEach((key) => {
        if (this.snoozed[key] > now && !snoozed[key]) snoozed[key] = this.snoozed[key];
      });
      this.snoozed = snoozed;
      try {
        localStorage.setItem(this.storageKey(), JSON.stringify(snoozed));
      } catch (e) {
        // ignore storage errors
      }
    },
    readSnoozed() {
      try {
        // Stara verzija je čuvala niz u sessionStorage — više se ne koristi
        sessionStorage.removeItem(SNOOZED_KEY);
        const value = JSON.parse(localStorage.getItem(this.storageKey()));
        return value && !Array.isArray(value) ? value : {};
      } catch (e) {
        return {};
      }
    },
  },
};
</script>

<style scoped>
.rating-sheet--mobile {
  border-radius: 20px 20px 0 0 !important;
  padding-bottom: calc(24px + env(safe-area-inset-bottom)) !important;
}
.rating-sheet--desktop {
  border-radius: 20px !important;
}
.sheet-handle {
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: #ddd;
}
.sheet-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fbebe3;
}
</style>

<style>
/* content-class v-dialoga je van scoped stabla */
.rating-popup-dialog {
  border-radius: 20px !important;
}
</style>
